import type { Role, UserDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InviteForm, PendingInvitations } from '../components/Invitations';
import { Badge, Card, ErrorMessage, formatDate, PageHeader } from '../components/ui';
import { MembershipActions } from './StudentsPage';

export function StaffPage() {
  const { user: me } = useAuth();
  const list = useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: async () => {
      const [instructors, owners] = await Promise.all([
        api.request<{ items: UserDto[] }>('/admin/users?role=instructor&limit=500'),
        api.request<{ items: UserDto[] }>('/admin/users?role=owner&limit=500'),
      ]);
      return [...owners.items, ...instructors.items];
    },
  });
  const setRole = useApiMutation<{ id: string; role: Role }>(
    ({ id, role }) => ({ path: `/admin/users/${id}`, method: 'PATCH', body: { role } }),
    [['admin', 'staff']],
  );
  return (
    <>
      <PageHeader
        title="Instructors"
        subtitle="Instructors can scan student QR codes and grade techniques A–C. Owners manage everything."
      />
      <Card title="Invite an instructor">
        <InviteForm role="instructor" onDone={() => void list.refetch()} />
      </Card>
      <PendingInvitations roles={['instructor', 'owner']} />
      <Card title="Staff">
        <ErrorMessage error={list.error ?? setRole.error} />
        <DataTable
          rows={list.data}
          rowKey={(u) => u.id}
          columns={[
            { key: 'name', header: 'Name', render: (u) => u.name },
            { key: 'email', header: 'Email', render: (u) => u.email },
            { key: 'status', header: 'Status', render: (u) => <Badge value={u.status} /> },
            {
              key: 'role',
              header: 'Role',
              render: (u) => (
                <select
                  aria-label={`Role for ${u.name}`}
                  value={u.role}
                  disabled={u.id === me?.id}
                  onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as Role })}
                >
                  <option value="instructor">Instructor</option>
                  <option value="owner">Owner</option>
                  <option value="student">Student</option>
                </select>
              ),
            },
            { key: 'login', header: 'Last sign-in', render: (u) => formatDate(u.lastLoginAt) },
            {
              key: 'actions',
              header: '',
              render: (u) =>
                u.id === me?.id ? (
                  <span className="muted">You</span>
                ) : (
                  <MembershipActions
                    userId={u.id}
                    status={u.status}
                    invalidate={[['admin', 'staff']]}
                  />
                ),
            },
          ]}
        />
      </Card>
    </>
  );
}
